/* Mirror Shella's existing option state into the accessibility tree. */
(() => {
  'use strict';
  if (!document.body.hasAttribute('data-drsknn-product-design')) return;
  const observers = new Map();
  const selector = '.product-page-info [data-js-product-options]';
  function sync(container) {
    for (const button of container.querySelectorAll('button[data-drsknn-variant-choice]')) {
      const pressed = String(button.classList.contains('active'));
      const disabled = String(button.classList.contains('disabled') || button.classList.contains('disabled-hidden'));
      if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
      if (button.getAttribute('aria-disabled') !== disabled) button.setAttribute('aria-disabled', disabled);
    }
  }
  function initialize(root) {
    const containers = root.matches?.(selector) ? [root] : Array.from(root.querySelectorAll(selector));
    for (const container of containers) {
      if (observers.has(container)) continue;
      sync(container);
      const observer = new MutationObserver(() => sync(container));
      // Theme changes only class names here. ARIA writes are outside this filter.
      observer.observe(container, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
      observers.set(container, observer);
    }
  }
  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [container, observer] of observers) {
      if (event.target.contains(container)) {
        observer.disconnect();
        observers.delete(container);
      }
    }
  });
})();

/* Product-page cart drawer: observe the existing popup/cart lifecycle. */
(() => {
  'use strict';
  if (!document.body.hasAttribute('data-drsknn-product-design') || typeof window === 'undefined') return;
  const selector = '[data-js-popup-name="cart"]';
  const focusable = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let cart = null, observer = null, active = false, opener = null, pendingOpener = null, lastFocused = null;
  let background = [], eventsBound = false;
  function visible(node) {
    return !!(node?.isConnected && node.getClientRects().length && !node.closest('[inert]') &&
      getComputedStyle(node).visibility !== 'hidden' && getComputedStyle(node).display !== 'none');
  }
  function set(node, key, value) { if (node.getAttribute(key) !== value) node.setAttribute(key, value); }
  function nativeButton(node, label) {
    let button = node;
    if (node.tagName !== 'BUTTON') {
      button = document.createElement('button');
      for (const attr of Array.from(node.attributes)) button.setAttribute(attr.name, attr.value);
      while (node.firstChild) button.appendChild(node.firstChild);
      node.replaceWith(button);
    }
    set(button, 'type', 'button'); set(button, 'aria-label', label);
    return button;
  }
  function prepare() {
    if (!cart) return;
    set(cart, 'role', 'dialog'); set(cart, 'aria-modal', 'true'); set(cart, 'aria-label', 'Shopping bag'); set(cart, 'tabindex', '-1');
    for (const close of cart.querySelectorAll('.popup-cart__close[data-js-popup-close]')) nativeButton(close, 'Close shopping bag');
    for (const item of cart.querySelectorAll('.product-cart')) {
      const title = item.querySelector('.product-cart__title')?.textContent.trim() || 'item';
      for (const input of item.querySelectorAll('input[name="updates[]"]')) set(input, 'aria-label', 'Quantity for ' + title);
      for (const control of item.querySelectorAll('[data-control]')) {
        const direction = control.getAttribute('data-control') === '+' ? 'Increase' : 'Decrease';
        nativeButton(control, direction + ' quantity for ' + title);
      }
    }
  }
  function focusClose() {
    if (!active || !cart?.isConnected) return;
    const close = cart.querySelector('.popup-cart__close[data-js-popup-close]');
    (visible(close) ? close : cart).focus({ preventScroll: true });
  }
  function activate() {
    if (!cart?.classList.contains('show')) return;
    prepare();
    if (!active) {
      active = true; opener = pendingOpener || document.activeElement; pendingOpener = null;
      let branch = cart;
      while (branch.parentElement) {
        for (const sibling of Array.from(branch.parentElement.children)) {
          if (sibling === branch || /^(SCRIPT|STYLE|LINK)$/.test(sibling.tagName)) continue;
          background.push({ node: sibling, wasInert: sibling.hasAttribute('inert') });
          sibling.setAttribute('inert', '');
        }
        if (branch.parentElement === document.body) break;
        branch = branch.parentElement;
      }
    }
    const shell = cart.closest('.js-popup');
    if (shell) set(shell, 'tabindex', '-1');
    if (!cart.contains(document.activeElement) || !visible(document.activeElement)) focusClose();
  }
  function release() {
    if (!active) return;
    active = false;
    for (const item of background) if (!item.wasInert) item.node.removeAttribute('inert');
    background = [];
    const shell = cart?.closest('.js-popup');
    if (shell) set(shell, 'tabindex', '-1');
    if (visible(opener) && !opener.disabled) opener.focus({ preventScroll: true });
    opener = null; lastFocused = null;
  }
  function sync() {
    prepare();
    if (cart?.classList.contains('show')) {
      activate();
      // AJAX cart refreshes replace the focused close/quantity/remove node.
      if (lastFocused && !lastFocused.isConnected) focusClose();
    } else release();
  }
  function initialize() {
    const next = document.querySelector(selector);
    if (next !== cart) {
      release(); observer?.disconnect(); cart = next; observer = null;
      if (cart) {
        observer = new MutationObserver(sync);
        observer.observe(cart, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
        sync();
      }
    }
    const $ = window.jQueryTheme || window.jQuery;
    if (!eventsBound && $) {
      eventsBound = true;
      $(document).on('cart.call.before.drsknnProductCart', selector, (_event, _popup, target) => {
        pendingOpener = target?.[0] || pendingOpener || document.activeElement;
        initialize(); prepare();
      });
      $(document).on('cart.call.visible.drsknnProductCart cart.call.after.drsknnProductCart', selector, () => { initialize(); activate(); });
      $(document).on('cart.close.after.drsknnProductCart', selector, release);
    }
  }
  document.addEventListener('theme:cart::added', event => { pendingOpener = event.detail?.button || document.activeElement; });
  document.addEventListener('click', event => {
    const target = event.target.closest?.('[data-js-popup-button="cart"]');
    if (target) pendingOpener = target;
  }, true);
  document.addEventListener('keydown', event => {
    if (!active) return;
    if (event.key === 'Escape') {
      const close = cart.querySelector('.popup-cart__close[data-js-popup-close]');
      if (close) { event.preventDefault(); event.stopPropagation(); close.click(); }
    } else if (event.key === 'Tab') {
      const controls = Array.from(cart.querySelectorAll(focusable)).filter(visible);
      const first = controls[0], last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); cart.focus({ preventScroll: true }); }
      else if (!cart.contains(document.activeElement) || document.activeElement === cart || (event.shiftKey && document.activeElement === first)) {
        event.preventDefault(); (event.shiftKey ? last : first).focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus({ preventScroll: true });
      }
    }
  }, true);
  document.addEventListener('focusin', event => {
    if (!active) return;
    if (cart.contains(event.target)) lastFocused = event.target;
    else focusClose();
  });
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => {
    if (cart && event.target.contains(cart)) { release(); observer?.disconnect(); cart = null; observer = null; }
  });
  initialize();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  // Shella may expose its jQuery alias only when its deferred assets finish.
  window.theme?.AssetsLoader?.onPageLoaded?.(initialize);
})();

/* Keep native purchase choices aligned with Shella's actual selected variant. */
(() => {
  'use strict';
  if (typeof customElements === 'undefined' || customElements.get('drsknn-purchase-options')) return;
  customElements.define('drsknn-purchase-options', class extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('select[data-drsknn-purchase-choice]');
      this.planInput = this.querySelector('input[data-drsknn-selling-plan]');
      this.product = this.closest('[data-js-product]');
      this.form = this.closest('form');
      if (!this.select || !this.planInput || !this.product || !this.form) return;
      this.onChoice = () => this.syncPlanInput();
      this.select.addEventListener('change', this.onChoice);
      this.requiresPlan = this.dataset.requiresPlan === 'true';
      this.guard = event => {
        if (event.type === 'click' && !event.target.closest('[data-js-product-button-add-to-cart], .shopify-payment-button')) return;
        if (this.requiresPlan && !this.select.value) {
          event.preventDefault(); event.stopImmediatePropagation();
          this.select.reportValidity(); this.select.focus({ preventScroll: true });
        }
      };
      this.form.addEventListener('click', this.guard, true);
      this.form.addEventListener('submit', this.guard, true);
      this.observer = new MutationObserver(() => this.syncVariant());
      this.observer.observe(this.product, { attributes: true, attributeFilter: ['data-product-variant-id'] });
      this.syncVariant();
    }
    syncVariant() {
      const variant = this.product.getAttribute('data-product-variant-id') || this.dataset.currentVariant;
      const previous = this.select.value;
      for (const group of this.select.querySelectorAll('optgroup[data-variant-id]')) {
        group.disabled = group.hidden = group.dataset.variantId !== variant;
        if (!group.disabled && !this.requiresPlan) {
          const oneTime = this.select.querySelector('option[value=""]');
          if (oneTime && group.dataset.oneTimePrice) oneTime.textContent = 'One-time purchase — ' + group.dataset.oneTimePrice;
        }
      }
      const valid = [];
      for (const option of this.select.querySelectorAll('option[data-variant-id]')) {
        option.disabled = option.hidden = option.dataset.variantId !== variant;
        if (!option.disabled) valid.push(option);
      }
      const chosen = valid.find(option => option.value === previous) || (this.requiresPlan ? valid[0] : this.select.querySelector('option[value=""]'));
      // The same plan ID can appear under multiple variants; select the exact option.
      this.select.selectedIndex = chosen ? Array.from(this.select.options).indexOf(chosen) : -1;
      this.select.required = this.requiresPlan;
      this.select.setCustomValidity(this.requiresPlan && !valid.length ? 'No purchase option is available for this variant.' : '');
      if (this.select.value !== previous || this.currentVariant !== variant) this.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.currentVariant = variant;
      this.syncPlanInput();
    }
    syncPlanInput() {
      const option = this.select.options[this.select.selectedIndex];
      const plan = option && !option.disabled ? this.select.value : '';
      // Shopify observes product-form value attributes; the hidden input is the
      // documented integration and is the sole successful selling_plan field.
      if (this.planInput.value !== plan) {
        this.planInput.value = plan;
        this.planInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.select?.removeEventListener('change', this.onChoice);
      this.form?.removeEventListener('click', this.guard, true);
      this.form?.removeEventListener('submit', this.guard, true);
    }
  });
})();

/* Keep successful product form fields aligned even if a theme display callback fails. */
(() => {
  'use strict';
  if (typeof window === 'undefined' || !document.body.hasAttribute('data-drsknn-product-design')) return;
  const forms = new Map();
  const selector = '.product-page-info form[data-js-product-form]';
  function initialize(root) {
    const candidates = root.matches?.(selector) ? [root] : Array.from(root.querySelectorAll(selector));
    for (const form of candidates) {
      if (forms.has(form)) continue;
      const product = form.closest('[data-js-product]');
      const select = form.querySelector('select[name="id"][data-js-product-variants]');
      if (!product || !select) continue;
      const sync = () => {
        if (product.querySelector('[data-property][data-disable-auto-select]')) return false;
        const variant = product.getAttribute('data-product-variant-id');
        const option = Array.from(select.options).find(item => item.value === variant);
        if (!option) return false;
        if (select.value !== variant) {
          select.value = variant;
          // Refresh Shopify's form observer after both successful fields agree.
          form.querySelector('[data-js-product-variant-input]')?.setAttribute('value', variant);
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return !option.disabled;
      };
      const guard = event => {
        if (event.type === 'click' && !event.target.closest('[data-js-product-button-add-to-cart], .shopify-payment-button')) return;
        if (!sync()) {
          event.preventDefault(); event.stopImmediatePropagation();
          form.querySelector('[data-drsknn-variant-choice]')?.focus({ preventScroll: true });
        }
      };
      const observer = new MutationObserver(sync);
      observer.observe(product, { attributes: true, attributeFilter: ['data-product-variant-id'] });
      form.addEventListener('click', guard, true);
      form.addEventListener('submit', guard, true);
      forms.set(form, { observer, guard });
      sync();
    }
  }
  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [form, state] of forms) if (event.target.contains(form)) {
      state.observer.disconnect();
      form.removeEventListener('click', state.guard, true);
      form.removeEventListener('submit', state.guard, true);
      forms.delete(form);
    }
  });
})();

/* Load the pricing dependency before the first product option interaction. */
(() => {
  'use strict';
  if (!document.body.hasAttribute('data-drsknn-product-design')) return;
  const loader = window.theme?.AssetsLoader;
  if (!loader) return;
  loader.require('scripts', 'theme_pt2');
  let pending = null, loading = false;
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-drsknn-variant-choice]');
    if (!button?.closest('.product-page-info') || button.disabled || button.getAttribute('aria-disabled') === 'true' || window.theme.ProductPrice) return;
    // The legacy option handler calls ProductPrice before updating its form.
    event.preventDefault(); event.stopImmediatePropagation();
    pending = button;
    if (loading) return;
    loading = true;
    loader.loadManually([['scripts', 'theme_pt2']], () => {
      const chosen = pending;
      pending = null; loading = false;
      if (window.theme.ProductPrice && chosen?.isConnected && !chosen.disabled && chosen.getAttribute('aria-disabled') !== 'true') chosen.click();
    });
  }, true);
})();
